import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation, useNavigationState, useRoute, useFocusEffect } from '@react-navigation/native';
import i18n from 'i18n-js';

import {
  getMachineBackgroundByState,
  machineDetailsConditions,
  navigateBack,
  navigateTo,
  ALL_MACHINES,
} from '../../../../../helpers';
import { Machine } from '@interfaces/machines';
import { OperationSearchItem } from '@interfaces/search';
import { OPERATOR_PANEL_INFORMATION_SCREENS, scheduledProductionOperationIdParam, SCHEDULED_FILTER_TYPE_MAP } from '@components/operatorPanel/constants/operatorPanel.constants';
import { LevelSubScreen } from '@v2/pages/common/enums';
import { ColorFunction } from '@styles/colors/types';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

export interface FilterOption {
  type: 'machine' | 'operation' | 'all';
  id: number | null;
  name: string;
}

export interface UseManageOperatorPanelScheduledParams {
  C: ColorFunction;
  selectedMachine?: Machine;
  machines: Machine[];
  getMachines: (formatResult?: boolean, preselectedId?: number) => Promise<Machine[]>;
  setSelectedMachine: any;
  setScreenToBackFromJobPerspective: (params: Record<string, unknown>) => void;
  shouldTrackSelectedMachine?: boolean;
  onScheduledJobPress?: (job: OperationSearchItem, itemType: string, levelSubScreen: LevelSubScreen) => void;
  customBg?: string;
}

export const buildFilterOptions = (selectedMachine?: Machine): FilterOption[] => {
  const options: FilterOption[] = [];

  if (selectedMachine?.fullName) {
    options.push({
      type: 'machine',
      id: selectedMachine.id,
      name: selectedMachine.fullName,
    });
  }

  if (selectedMachine?.operations) {
    options.push({
      type: 'operation',
      id: null,
      name: i18n.t('Matched Operations'),
    });
  }

  options.push({
    type: 'all',
    id: null,
    name: i18n.t('All Operations'),
  });

  return options;
};

export const buildODataFilter = (
  selectedFilterObject: FilterOption | null,
  machines: Machine[],
  selectedMachine?: Machine,
): string => {
  const baseFilter = "Status ne 'Finished'";

  if (!selectedFilterObject) return baseFilter;

  if (selectedFilterObject.type === 'machine') {
    const machinesIds = selectedFilterObject.id === ALL_MACHINES ? machines.map((m) => m.id) : [selectedFilterObject.id];

    return `${baseFilter} and Machines/any(machine: (${machinesIds
      .map((id) => `machine/MachineId eq ${id}`)
      .join(' or ')}) and (machine/ LinkType eq 'Preferred' or machine/LinkType eq 'ProducedOn'))`;
  } else if (selectedFilterObject.type === 'operation') {
    const storage: string[] = [];
    if (selectedMachine?.operations) {
      selectedMachine.operations.forEach((operation) => {
        storage.push(`OperationId eq ${operation.operationId}`);
      });
    }
    const operations = storage.join(' or ');

    return `${baseFilter}${operations ? ` and (${operations})` : ''}`;
  } else {
    return baseFilter;
  }
};

export const buildStatusBarBg = (C: ColorFunction, selectedMachine?: Machine): string => {
  const { ignoredNcFile, machineActive, machineOffline } = machineDetailsConditions({
    status: selectedMachine?.machineStatus?.status,
    ncFileStatus: selectedMachine?.machineStatus?.ncFileStatus,
    subStatus: selectedMachine?.machineStatus?.subStatus,
    ncFileExist: !!selectedMachine?.machineStatus?.ncFile,
  });

  return C(getMachineBackgroundByState({ ignoredNcFile, machineActive, machineOffline, status: selectedMachine?.machineStatus?.status }), false);
};

export const useManageOperatorPanelScheduled = ({
  C,
  selectedMachine,
  machines,
  getMachines,
  setSelectedMachine,
  setScreenToBackFromJobPerspective,
  shouldTrackSelectedMachine = false,
  onScheduledJobPress,
  customBg,
}: UseManageOperatorPanelScheduledParams) => {
  const navigation = useNavigation<any>();
  const route = useRoute();
  const navigationStateRoutes = useNavigationState((state) => state.routes);

  const [queryId, setQueryId] = useState<string | undefined>();
  const [selectedFilter, setSelectedFilter] = useState<string>(selectedMachine?.fullName || '');
  const [selectedFilterObject, setSelectedFilterObject] = useState<FilterOption | null>(
    selectedMachine?.fullName
      ? {
          type: 'machine',
          id: selectedMachine.id,
          name: selectedMachine.fullName,
        }
      : null
  );

  useFocusEffect(
    useCallback(() => {
      if (!shouldTrackSelectedMachine || !selectedMachine) return;
      const someMachineSelected = machines.some((m) => m.fullName === selectedFilter);
      if (someMachineSelected && selectedMachine.fullName !== selectedFilter) {
        setSelectedFilter(selectedMachine?.fullName);
      }
    }, [selectedMachine?.id, shouldTrackSelectedMachine])
  );

  useEffect(() => {
    if (!shouldTrackSelectedMachine || !selectedMachine) return;
    setSelectedFilterObject({
      type: 'machine',
      id: selectedMachine.id,
      name: selectedMachine.fullName,
    });
    setSelectedFilter(selectedMachine.fullName);
  }, [selectedMachine?.id, selectedMachine?.fullName, shouldTrackSelectedMachine]);

  const filterOptions = useMemo(
    () => buildFilterOptions(selectedMachine),
    [selectedMachine?.fullName, selectedMachine?.id, selectedMachine?.operations]
  );

  const oDataFilter = useMemo(
    () => buildODataFilter(selectedFilterObject, machines, selectedMachine),
    [selectedFilterObject, selectedMachine?.operations, machines]
  );

  const handleOperationSelect = (item: OperationSearchItem, itemType: string, levelSubScreen: LevelSubScreen) => {
    if (onScheduledJobPress) {
      onScheduledJobPress(item, itemType, levelSubScreen);
      return;
    }

    setScreenToBackFromJobPerspective(route);
    navigateTo(OPERATOR_PANEL_INFORMATION_SCREENS.Information, {
      screen: OPERATOR_PANEL_INFORMATION_SCREENS.Jobs,
      params: { [scheduledProductionOperationIdParam]: item.productionOperationId, id: selectedMachine?.id },
    });
  };

  const handleFilterClick = (filter: FilterOption) => {
    trackEvent(TRACKING_EVENTS.MACHINE_SCHEDULED, {
      action: 'filterChanged',
      selectedFilter: SCHEDULED_FILTER_TYPE_MAP[filter.type] || 'all',
    });
    setSelectedFilter(filter.name);
    setSelectedFilterObject(filter);
  };

  useEffect(() => {
    if (!queryId || shouldTrackSelectedMachine) return;
    if (selectedMachine?.id === +queryId) return;

    (async () => {
      const fetchedMachines = await getMachines(true);
      const machine = fetchedMachines.find((m) => m.id === +queryId);

      if (machine) {
        setSelectedMachine(machine);
      } else {
        if (navigation.canGoBack()) {
          navigateBack();
        } else {
          navigateTo('OperatorPanel', { id: queryId });
        }
      }
    })();
  }, [route, selectedMachine, queryId, shouldTrackSelectedMachine]);

  useEffect(() => {
    const params = navigationStateRoutes[0]?.params as any;
    const paramsQueryId = params?.id;
    setQueryId(paramsQueryId);
  }, [navigationStateRoutes]);

  const onClose = () => {
    navigation.setParams({ hideNcFilePopup: undefined });
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigateTo('OperatorPanel', { id: queryId });
  };

  const isLight = C.scheme === 'light';
  const mainBg = customBg ? C(customBg) : C(`Background/${isLight ? 'Light' : 'Dark'}/Panel`, false);

  const statusBarBg = useMemo(
    () => buildStatusBarBg(C, selectedMachine),
    [selectedMachine]
  );

  const shouldDisplayFilterSelectors = selectedFilterObject?.id !== ALL_MACHINES;

  return {
    filterOptions,
    oDataFilter,
    selectedFilter,
    selectedFilterObject,
    handleOperationSelect,
    handleFilterClick,
    onClose,
    mainBg,
    statusBarBg,
    shouldDisplayFilterSelectors,
    queryId,
  };
};
